import { createFileRoute } from '@tanstack/react-router'
import { useEffect, useState } from 'react'

export const Route = createFileRoute('/unsubscribe')({
  head: () => ({
    meta: [
      { title: 'Unsubscribe — CryptoBull Pro' },
      { name: 'robots', content: 'noindex' },
    ],
  }),
  component: UnsubscribePage,
})

type State =
  | { kind: 'loading' }
  | { kind: 'ready' }
  | { kind: 'already' }
  | { kind: 'invalid' }
  | { kind: 'success' }
  | { kind: 'error'; message: string }

function UnsubscribePage() {
  const [state, setState] = useState<State>({ kind: 'loading' })
  const token =
    typeof window !== 'undefined'
      ? new URLSearchParams(window.location.search).get('token')
      : null

  useEffect(() => {
    if (!token) {
      setState({ kind: 'invalid' })
      return
    }
    fetch(`/email/unsubscribe?token=${encodeURIComponent(token)}`)
      .then(async (r) => {
        const data = await r.json().catch(() => ({}))
        if (!r.ok) return setState({ kind: 'invalid' })
        if (data.valid) return setState({ kind: 'ready' })
        if (data.reason === 'already_unsubscribed') return setState({ kind: 'already' })
        setState({ kind: 'invalid' })
      })
      .catch(() => setState({ kind: 'error', message: 'Network error' }))
  }, [token])

  async function confirm() {
    if (!token) return
    setState({ kind: 'loading' })
    try {
      const r = await fetch('/email/unsubscribe', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify({ token }),
      })
      const data = await r.json().catch(() => ({}))
      if (data.success) return setState({ kind: 'success' })
      if (data.reason === 'already_unsubscribed') return setState({ kind: 'already' })
      setState({ kind: 'error', message: data.error ?? 'Failed to unsubscribe' })
    } catch {
      setState({ kind: 'error', message: 'Network error' })
    }
  }

  return (
    <main className="min-h-screen flex items-center justify-center bg-background px-4">
      <div className="w-full max-w-md rounded-2xl border border-border bg-card p-8 shadow-sm">
        <div className="mb-6 border-b-2 border-primary/80 pb-3">
          <h1 className="text-xl font-bold tracking-tight">CryptoBull Pro</h1>
        </div>
        {state.kind === 'loading' && (
          <p className="text-sm text-muted-foreground">Checking your unsubscribe link…</p>
        )}
        {state.kind === 'invalid' && (
          <>
            <h2 className="text-lg font-semibold mb-2">Link not valid</h2>
            <p className="text-sm text-muted-foreground">
              This unsubscribe link is invalid or has expired. You can manage
              email notifications from your account settings.
            </p>
          </>
        )}
        {state.kind === 'already' && (
          <>
            <h2 className="text-lg font-semibold mb-2">Already unsubscribed</h2>
            <p className="text-sm text-muted-foreground">
              This email address has already been unsubscribed from CryptoBull Pro emails.
            </p>
          </>
        )}
        {state.kind === 'ready' && (
          <>
            <h2 className="text-lg font-semibold mb-2">Unsubscribe from emails</h2>
            <p className="text-sm text-muted-foreground mb-6">
              Confirm to stop receiving emails from CryptoBull Pro at this address.
              You can still manage in-app notifications in Settings.
            </p>
            <button
              onClick={confirm}
              className="inline-flex items-center justify-center rounded-md bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground hover:opacity-90"
            >
              Confirm unsubscribe
            </button>
          </>
        )}
        {state.kind === 'success' && (
          <>
            <h2 className="text-lg font-semibold mb-2">You\u2019re unsubscribed</h2>
            <p className="text-sm text-muted-foreground">
              You won\u2019t receive further emails from CryptoBull Pro at this address.
            </p>
          </>
        )}
        {state.kind === 'error' && (
          <>
            <h2 className="text-lg font-semibold mb-2">Something went wrong</h2>
            <p className="text-sm text-muted-foreground">{state.message}</p>
          </>
        )}
      </div>
    </main>
  )
}